import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useDrivers, useSvcMutation, useTransportRequests, useTripTypes, useVehicles, type TransportRequest, type TripStatus } from '../api/services';
import { formatGhs, formatHotelTime, hotelLocalToIso, parseCedis, type Tone } from '../format';
import { Badge, Button, Empty, ErrorNote, Field, Loading, Modal, useReport, useToast } from '../ui/kit';
import { PageHead } from './shared';
import { InlinePrice, ServicePayDialog } from './ServicePay';
import { RefundDialog } from './Refund';

const STATUS: Record<TripStatus, { label: string; tone: Tone }> = {
  REQUESTED: { label: 'Requested', tone: 'warn' },
  CONFIRMED: { label: 'Confirmed', tone: 'info' },
  DISPATCHED: { label: 'On the road', tone: 'brand' },
  COMPLETED: { label: 'Completed', tone: 'good' },
  CANCELLED: { label: 'Cancelled', tone: '' },
};

type Tab = 'open' | 'today' | 'unpaid';

export function Transport() {
  const { me, can } = useAuth();
  const today = me?.property.today ?? '';
  const [tab, setTab] = useState<Tab>('open');
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [setup, setSetup] = useState(false);
  const query = tab === 'open' ? '' : tab === 'today' ? `?date=${today}` : '?unpaid=1';
  const list = useTransportRequests(query);
  const rows = list.data ?? [];
  const open = rows.find((r) => r.id === openId) ?? null;

  return (
    <div className="page">
      <PageHead eyebrow="Transport" title="Trips">
        {can('transport.book') ? (
          <Button variant="primary" onClick={() => setCreating(true)}>
            New trip
          </Button>
        ) : null}
        {can('transport.manage') ? <Button onClick={() => setSetup(true)}>Vehicles and prices</Button> : null}
      </PageHead>

      <div className="seg" role="group" aria-label="Show">
        <button type="button" aria-pressed={tab === 'open'} onClick={() => setTab('open')}>
          To do
        </button>
        <button type="button" aria-pressed={tab === 'today'} onClick={() => setTab('today')}>
          Today
        </button>
        <button type="button" aria-pressed={tab === 'unpaid'} onClick={() => setTab('unpaid')}>
          Unpaid
        </button>
      </div>

      {list.isLoading ? (
        <Loading rows={3} />
      ) : list.error ? (
        <ErrorNote error={list.error} retry={() => void list.refetch()} />
      ) : rows.length === 0 ? (
        <div className="card flat">
          <Empty icon="check" title={tab === 'unpaid' ? 'Nothing waiting for payment' : 'No trips here'} />
        </div>
      ) : (
        <div className="list">
          {rows.map((r) => (
            <button key={r.id} type="button" className="item" style={{ textAlign: 'left', cursor: 'pointer' }} onClick={() => setOpenId(r.id)}>
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span className="title">
                  {formatHotelTime(r.pickupAt, me?.property.timezone ?? 'Africa/Accra')} · {r.customerName}
                </span>
                <span className="row">
                  {r.status !== 'CANCELLED' && r.status !== 'REQUESTED' ? <Badge tone={r.refunded ? '' : r.paid ? 'good' : 'warn'}>{r.refunded ? 'Refunded' : r.paid ? 'Paid' : 'Unpaid'}</Badge> : null}
                  <Badge tone={STATUS[r.status].tone}>{STATUS[r.status].label}</Badge>
                </span>
              </div>
              <span className="small muted">
                {r.pickup} → {r.dropoff} · {r.passengers} passenger{r.passengers === 1 ? '' : 's'} · {r.tripName}
              </span>
              <span className="small muted">{r.driver ? `${r.driver.name}${r.vehicle ? ` · ${r.vehicle.name}` : ''}` : 'No driver yet'}</span>
            </button>
          ))}
        </div>
      )}

      <TripDialog trip={open} onClose={() => setOpenId(null)} />
      {creating ? <NewTripDialog onClose={() => setCreating(false)} /> : null}
      {setup ? <SetupDialog onClose={() => setSetup(false)} /> : null}
    </div>
  );
}

/* --------------------------------------------------------------- one trip */
function TripDialog({ trip: r, onClose }: { trip: TransportRequest | null; onClose: () => void }) {
  const { me, can } = useAuth();
  const toast = useToast();
  const report = useReport();
  const vehicles = useVehicles();
  const drivers = useDrivers(can('transport.dispatch'));
  const [paying, setPaying] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState('');
  const act = useSvcMutation((v: { path: string; method?: 'POST' | 'PATCH'; body?: Record<string, unknown> }) => api(`/transport/requests/${r!.id}/${v.path}`, { method: v.method ?? 'POST', body: v.body ?? {} }));
  useEffect(() => {
    if (!r) {
      setCancelling(false);
      setReason('');
    }
  }, [r]);
  if (!r) return <Modal open={false} onClose={onClose} title="Trip">{null}</Modal>;

  const run = (path: string, done?: string, body?: Record<string, unknown>, method?: 'POST' | 'PATCH', then?: () => void) =>
    act.mutate({ path, ...(body ? { body } : {}), ...(method ? { method } : {}) }, { onError: report, onSuccess: () => { if (done) toast.ok(done); then?.(); } });
  const editable = r.status === 'REQUESTED' || r.status === 'CONFIRMED';
  const payable = can('service.settle') && !r.paid && r.price > 0 && (r.status === 'CONFIRMED' || r.status === 'DISPATCHED' || r.status === 'COMPLETED');

  return (
    <>
      <Modal open={!paying && !refunding} onClose={onClose} title={r.tripName}>
        <div className="stack">
          <div className="row">
            <Badge tone={STATUS[r.status].tone}>{STATUS[r.status].label}</Badge>
            {r.status !== 'CANCELLED' && r.status !== 'REQUESTED' ? r.refunded ? <Badge>Refunded</Badge> : <Badge tone={r.paid ? 'good' : 'warn'}>{r.paid ? `Paid by ${r.paidWith?.toLowerCase()}` : 'Not paid'}</Badge> : null}
          </div>
          <dl className="kv">
            <dt>Reference</dt>
            <dd className="num">{r.reference}</dd>
            <dt>Pickup</dt>
            <dd>{formatHotelTime(r.pickupAt, me?.property.timezone ?? 'Africa/Accra')}</dd>
            <dt>From</dt>
            <dd>{r.pickup}</dd>
            <dt>To</dt>
            <dd>{r.dropoff}</dd>
            <dt>Passenger</dt>
            <dd>
              {r.customerName} · {r.passengers}
              {r.phone ? (
                <>
                  {' '}
                  · <a href={`tel:${r.phone}`}>{r.phone}</a>
                </>
              ) : null}
            </dd>
            <dt>Price</dt>
            <dd className="num">{formatGhs(r.price)}</dd>
          </dl>
          {r.notes ? <p className="small muted">{r.notes}</p> : null}
          {r.cancelReason ? <p className="small muted">Cancelled: {r.cancelReason}</p> : null}

          {can('transport.dispatch') && editable ? (
            <div className="grid-2">
              <Field id="tr-veh" label="Vehicle">
                <select id="tr-veh" className="select" value={r.vehicle?.id ?? ''} disabled={act.isPending} onChange={(e) => run('assign', 'Vehicle assigned', { vehicleId: e.target.value || null }, 'PATCH')}>
                  <option value="">Not assigned</option>
                  {(vehicles.data ?? []).map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name} ({v.seats} seats)
                    </option>
                  ))}
                </select>
              </Field>
              <Field id="tr-drv" label="Driver">
                <select id="tr-drv" className="select" value={r.driver?.id ?? ''} disabled={act.isPending} onChange={(e) => run('assign', 'Driver assigned', { driverId: e.target.value || null }, 'PATCH')}>
                  <option value="">Not assigned</option>
                  {(drivers.data ?? []).map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          ) : (
            <p className="small muted">{r.driver ? `${r.driver.name}${r.vehicle ? ` · ${r.vehicle.name}` : ''}` : 'No driver assigned yet'}</p>
          )}

          {cancelling ? (
            <form
              className="stack"
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                if (reason.trim().length >= 3) run('cancel', 'Trip cancelled', { reason: reason.trim() }, 'POST', () => setCancelling(false));
              }}
            >
              <Field id="tr-why" label="Why is it cancelled?">
                <input id="tr-why" className="input" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
              </Field>
              <div className="modal-foot">
                <Button type="button" onClick={() => setCancelling(false)}>
                  Back
                </Button>
                <Button variant="danger" type="submit" disabled={reason.trim().length < 3} loading={act.isPending}>
                  Cancel trip
                </Button>
              </div>
            </form>
          ) : (
            <div className="stack">
              {r.status === 'REQUESTED' && can('transport.book') ? (
                <Button variant="primary" block loading={act.isPending} onClick={() => run('confirm', 'Trip confirmed')}>
                  Confirm trip
                </Button>
              ) : null}
              {r.status === 'CONFIRMED' && can('transport.dispatch') ? (
                <Button variant="primary" block loading={act.isPending} disabled={!r.vehicle || !r.driver} onClick={() => run('dispatch', 'On the road')}>
                  {r.vehicle && r.driver ? 'Dispatch' : 'Assign a vehicle and driver to dispatch'}
                </Button>
              ) : null}
              {r.status === 'DISPATCHED' && can('transport.dispatch') ? (
                <Button variant="primary" block loading={act.isPending} onClick={() => run('complete', 'Trip completed')}>
                  Mark completed
                </Button>
              ) : null}
              {can('refund.issue') && r.paid && !r.refunded && r.status !== 'CANCELLED' ? (
                <Button variant="danger" block onClick={() => setRefunding(true)}>
                  Refund {formatGhs(r.price)}
                </Button>
              ) : null}
              {payable ? (
                <Button block onClick={() => setPaying(true)}>
                  Take payment · {formatGhs(r.price)}
                </Button>
              ) : null}
              {editable && can('transport.book') ? (
                <Button variant="danger" block disabled={r.paid && !r.refunded} onClick={() => setCancelling(true)}>
                  Cancel trip
                </Button>
              ) : null}
              <Button block onClick={onClose}>
                Close
              </Button>
            </div>
          )}
        </div>
      </Modal>
      {refunding ? <RefundDialog title={`Refund ${r.reference}`} path={`/transport/requests/${r.id}/refund`} paid={[{ method: r.paidWith ?? 'CASH', amount: r.price }]} withCancel={r.status === 'REQUESTED' || r.status === 'CONFIRMED'} onClose={() => setRefunding(false)} /> : null}
      {paying ? <ServicePayDialog title={`Pay ${r.reference}`} payPath={`/transport/requests/${r.id}/pay`} total={r.price} bookingId={r.bookingId} onClose={() => setPaying(false)} /> : null}
    </>
  );
}

/* -------------------------------------------------------------- new trip */
function NewTripDialog({ onClose }: { onClose: () => void }) {
  const { me } = useAuth();
  const tz = me?.property.timezone ?? 'Africa/Accra';
  const trips = useTripTypes();
  const toast = useToast();
  const report = useReport();
  const [tripTypeId, setTripTypeId] = useState('');
  const [pickup, setPickup] = useState(me?.property.name ?? '');
  const [dropoff, setDropoff] = useState('');
  const [when, setWhen] = useState('');
  const [passengers, setPassengers] = useState(1);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const make = useSvcMutation((body: Record<string, unknown>) => api('/transport/requests', { method: 'POST', body }));
  useEffect(() => {
    if (!tripTypeId && trips.data?.[0]) setTripTypeId(trips.data[0].id);
  }, [trips.data, tripTypeId]);

  const trip = trips.data?.find((t) => t.id === tripTypeId);
  const valid = !!tripTypeId && pickup.trim().length >= 2 && dropoff.trim().length >= 2 && !!when && name.trim().length >= 2;

  return (
    <Modal open onClose={onClose} title="New trip">
      <form
        className="stack"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (!valid) return;
          make.mutate(
            { tripTypeId, pickup: pickup.trim(), dropoff: dropoff.trim(), pickupAt: hotelLocalToIso(when, tz), passengers, customerName: name.trim(), ...(phone.trim() ? { phone: phone.trim() } : {}) },
            { onError: report, onSuccess: () => { toast.ok('Trip requested'); onClose(); } },
          );
        }}
      >
        <Field id="nt-trip" label="Trip">
          <select id="nt-trip" className="select" value={tripTypeId} onChange={(e) => setTripTypeId(e.target.value)}>
            {(trips.data ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} · {formatGhs(t.price)}
              </option>
            ))}
          </select>
        </Field>
        <Field id="nt-when" label="Pickup date and time" hint="Hotel time">
          <input id="nt-when" className="input" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} />
        </Field>
        <div className="grid-2">
          <Field id="nt-from" label="Pickup place">
            <input id="nt-from" className="input" value={pickup} onChange={(e) => setPickup(e.target.value)} />
          </Field>
          <Field id="nt-to" label="Going to">
            <input id="nt-to" className="input" value={dropoff} onChange={(e) => setDropoff(e.target.value)} />
          </Field>
        </div>
        <div className="grid-2">
          <Field id="nt-name" label="Passenger name">
            <input id="nt-name" className="input" value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
          </Field>
          <Field id="nt-n" label="Passengers">
            <input id="nt-n" className="input" type="number" min={1} max={60} value={passengers} onChange={(e) => setPassengers(Math.max(1, Number(e.target.value) || 1))} />
          </Field>
        </div>
        <Field id="nt-phone" label="Phone (optional)">
          <input id="nt-phone" className="input" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </Field>
        {trip ? (
          <div className="total-row">
            <span>Price</span>
            <span className="amount num">{formatGhs(trip.price)}</span>
          </div>
        ) : null}
        {trips.data && trips.data.length === 0 ? <div className="banner bad">No trips are priced yet. A manager can add them under Vehicles and prices.</div> : null}
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={!valid} loading={make.isPending}>
            Request trip
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/* --------------------------------------------------- vehicles and trip prices */
function SetupDialog({ onClose }: { onClose: () => void }) {
  const vehicles = useVehicles(true);
  const trips = useTripTypes(true);
  const toast = useToast();
  const report = useReport();
  const [vName, setVName] = useState('');
  const [plate, setPlate] = useState('');
  const [seats, setSeats] = useState('4');
  const [tName, setTName] = useState('');
  const [tPrice, setTPrice] = useState('');
  const [tMins, setTMins] = useState('90');
  const addVehicle = useSvcMutation((body: Record<string, unknown>) => api('/transport/vehicles', { method: 'POST', body }));
  const patchVehicle = useSvcMutation((v: { id: string; body: Record<string, unknown> }) => api(`/transport/vehicles/${v.id}`, { method: 'PATCH', body: v.body }));
  const addTrip = useSvcMutation((body: Record<string, unknown>) => api('/transport/trip-types', { method: 'POST', body }));
  const patchTrip = useSvcMutation((v: { id: string; body: Record<string, unknown> }) => api(`/transport/trip-types/${v.id}`, { method: 'PATCH', body: v.body }));
  const price = parseCedis(tPrice);

  return (
    <Modal open onClose={onClose} title="Vehicles and trip prices">
      <div className="stack">
        <div className="eyebrow">Vehicles</div>
        <ul className="checklist">
          {(vehicles.data ?? []).map((v) => (
            <li key={v.id}>
              <div className="row" style={{ justifyContent: 'space-between', minHeight: 44 }}>
                <span>
                  {v.name} · {v.seats} seats{v.plate ? ` · ${v.plate}` : ''}
                  {v.active ? '' : ' (not in use)'}
                </span>
                <Button variant="ghost" onClick={() => patchVehicle.mutate({ id: v.id, body: { active: !v.active } }, { onError: report })}>
                  {v.active ? 'Retire' : 'Use again'}
                </Button>
              </div>
            </li>
          ))}
        </ul>
        <form
          className="stack"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            addVehicle.mutate({ name: vName.trim(), ...(plate.trim() ? { plate: plate.trim() } : {}), seats: Math.max(1, Number(seats) || 4) }, { onError: report, onSuccess: () => { toast.ok('Vehicle added'); setVName(''); setPlate(''); } });
          }}
        >
          <div className="grid-2">
            <Field id="vh-name" label="New vehicle">
              <input id="vh-name" className="input" value={vName} onChange={(e) => setVName(e.target.value)} placeholder="e.g. Hotel van" />
            </Field>
            <Field id="vh-seats" label="Seats">
              <input id="vh-seats" className="input" inputMode="numeric" value={seats} onChange={(e) => setSeats(e.target.value.replace(/\D/g, ''))} />
            </Field>
          </div>
          <Field id="vh-plate" label="Number plate (optional)">
            <input id="vh-plate" className="input" value={plate} onChange={(e) => setPlate(e.target.value)} />
          </Field>
          <Button type="submit" disabled={vName.trim().length < 2} loading={addVehicle.isPending}>
            Add vehicle
          </Button>
        </form>

        <div className="eyebrow">Trips and prices (GH₵, tax included)</div>
        <ul className="checklist">
          {(trips.data ?? []).map((t) => (
            <li key={t.id}>
              <div className="row" style={{ justifyContent: 'space-between', minHeight: 44 }}>
                <span className="row">
                  <span>
                    {t.name} · {t.durationMinutes} min{t.active ? '' : ' (hidden)'}
                  </span>
                  <InlinePrice value={t.price} pending={patchTrip.isPending} onSave={(price) => patchTrip.mutate({ id: t.id, body: { price } }, { onError: report, onSuccess: () => toast.ok('Price updated for new trips') })} />
                </span>
                <Button variant="ghost" onClick={() => patchTrip.mutate({ id: t.id, body: { active: !t.active } }, { onError: report })}>
                  {t.active ? 'Hide' : 'Show'}
                </Button>
              </div>
            </li>
          ))}
        </ul>
        <form
          className="stack"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            if (price !== null) addTrip.mutate({ name: tName.trim(), price, durationMinutes: Math.max(15, Number(tMins) || 90) }, { onError: report, onSuccess: () => { toast.ok('Trip added'); setTName(''); setTPrice(''); } });
          }}
        >
          <Field id="tp-name" label="New trip">
            <input id="tp-name" className="input" value={tName} onChange={(e) => setTName(e.target.value)} placeholder="e.g. Airport transfer" />
          </Field>
          <div className="grid-2">
            <Field id="tp-price" label="Price">
              <input id="tp-price" className="input" inputMode="decimal" value={tPrice} onChange={(e) => setTPrice(e.target.value)} />
            </Field>
            <Field id="tp-mins" label="Takes about (minutes)" hint="Keeps one driver off two trips">
              <input id="tp-mins" className="input" inputMode="numeric" value={tMins} onChange={(e) => setTMins(e.target.value.replace(/\D/g, ''))} />
            </Field>
          </div>
          <Button type="submit" disabled={tName.trim().length < 2 || price === null} loading={addTrip.isPending}>
            Add trip
          </Button>
        </form>
        <Button block onClick={onClose}>
          Done
        </Button>
      </div>
    </Modal>
  );
}
